"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setFieldAttr = void 0;
const command_manager_1 = require("../../command_manager");
const lodash_1 = require("lodash");
const store_1 = require("../../exports/store");
const selectors_1 = require("../../exports/store/selectors");
const field_types_1 = require("../../types/field_types");
const i18n_1 = require("../../exports/i18n");
const types_1 = require("../../types");
const field_1 = require("../common/field");
const field_2 = require("../../model/field");
const model_1 = require("../../model");
function generateLinkedFieldActions(context, snapshot, oldField, newField, datasheetId, deleteBrotherField, internalFix) {
    const actions = [];
    const linkedActions = [];
    const { model: state } = context;
    if (oldField.type === field_types_1.FieldType.Link && newField.type === field_types_1.FieldType.Link) {
        // If the associated table id has not changed, no related operations are required.
        if (oldField.property.foreignDatasheetId === newField.property.foreignDatasheetId) {
            return (0, field_1.setField)(context, snapshot, oldField, newField, datasheetId);
        }
    }
    if (oldField.type === field_types_1.FieldType.Link) {
        const clearedActions = (0, field_1.clearOldBrotherField)(context, oldField, deleteBrotherField);
        clearedActions && linkedActions.push(clearedActions);
    }
    if (newField.type === field_types_1.FieldType.Link) {
        const createdActions = (0, field_1.createNewBrotherField)(state, newField, datasheetId);
        createdActions && linkedActions.push(createdActions);
    }
    if (field_types_1.FieldType.Text === newField.type && (internalFix === null || internalFix === void 0 ? void 0 : internalFix.clearOneWayLinkCell)) {
        const fieldId = oldField.id;
        // Clean up the content of the one-way association cell
        if (snapshot.meta.fieldMap[fieldId]) {
            for (const recordId in snapshot.recordMap) {
                const action = model_1.DatasheetActions.setRecord2Action(snapshot, { recordId, fieldId, value: null });
                action && actions.push(action);
            }
        }
    }
    const newFieldData = (0, field_1.setField)(context, snapshot, oldField, newField, datasheetId);
    actions.push(...newFieldData.actions);
    return { actions: actions, linkedActions };
}
exports.setFieldAttr = {
    undoable: true,
    execute: (context, options) => {
        var _a;
        const { model: state } = context;
        const activeDatasheetId = store_1.Selectors.getActiveDatasheetId(state);
        const { fieldId, datasheetId = activeDatasheetId, deleteBrotherField, internalFix } = options;
        const newField = Object.assign({}, options.data);
        const snapshot = store_1.Selectors.getSnapshot(state, datasheetId);
        if (!snapshot || fieldId !== newField.id) {
            return null;
        }
        const fieldMap = (0, selectors_1.getFieldMap)(state, datasheetId);
        const oldField = fieldMap[fieldId];
        if (!oldField) {
            return null;
        }
        /* Check for duplicate names */
        const duplicate = Object.values(fieldMap).some(f => {
            return f.id !== options.fieldId && f.name === options.data.name;
        });
        if (duplicate) {
            throw new Error((0, i18n_1.t)(i18n_1.Strings.error_set_column_failed_duplicate_column_name));
        }
        if (oldField.type === field_types_1.FieldType.NotSupport ||
            newField.type === field_types_1.FieldType.NotSupport) {
            throw new Error((0, i18n_1.t)(i18n_1.Strings.error_set_column_failed_no_support_unknown_column));
        }
        if (oldField.type === newField.type &&
            oldField.name === newField.name &&
            oldField.desc === newField.desc &&
            oldField.required === newField.required &&
            (0, lodash_1.isEqual)(oldField.property, newField.property)) {
            return null;
        }
        // Compatible with errors caused by defaultValue of some online fields being null
        if ([field_types_1.FieldType.Currency, field_types_1.FieldType.Percent, field_types_1.FieldType.Number].includes(newField.type) &&
            newField.property.defaultValue === null) {
            newField.property = Object.assign(Object.assign({}, newField.property), { defaultValue: '' });
        }
        // AutoNumber needs to record the current view index
        if (newField.type === field_types_1.FieldType.AutoNumber) {
            const datasheet = (0, selectors_1.getDatasheet)(state);
            const viewIdx = snapshot.meta.views.findIndex(item => item.id === (datasheet === null || datasheet === void 0 ? void 0 : datasheet.activeView)) || 0;
            newField.property = Object.assign(Object.assign({}, newField.property), { viewIdx });
        }
        // Ensure that the properties of the following fields must have datasheetId
        if (!((_a = newField.property) === null || _a === void 0 ? void 0 : _a.datasheetId) &&
            [field_types_1.FieldType.AutoNumber, field_types_1.FieldType.CreatedBy, field_types_1.FieldType.CreatedTime, field_types_1.FieldType.LastModifiedBy, field_types_1.FieldType.LastModifiedTime].includes(newField.type)) {
            newField.property = Object.assign(Object.assign({}, newField.property), { datasheetId });
        }
        // When modifying the associated field, it is necessary to maintain the sibling field data of the associated table
        if (oldField.type === field_types_1.FieldType.Link || newField.type === field_types_1.FieldType.Link) {
            const validateFieldPropertyError = field_2.Field.bindContext(newField, state).validateProperty().error;
            if (validateFieldPropertyError) {
                throw new Error(`${(0, i18n_1.t)(i18n_1.Strings.error_set_column_failed_bad_property)}: ${validateFieldPropertyError.details.map(d => d.message).join(',\n')}`);
            }
            const result = generateLinkedFieldActions(context, snapshot, oldField, newField, datasheetId, deleteBrotherField, internalFix);
            const linkedActions = result.linkedActions || [];
            return {
                result: command_manager_1.ExecuteResult.Success,
                resourceId: datasheetId,
                resourceType: types_1.ResourceType.Datasheet,
                actions: result.actions,
                linkedActions,
            };
        }
        return {
            result: command_manager_1.ExecuteResult.Success,
            resourceId: datasheetId,
            resourceType: types_1.ResourceType.Datasheet,
            actions: (0, field_1.setField)(context, snapshot, oldField, newField, datasheetId).actions,
        };
    },
};
//# sourceMappingURL=set_field_attr.js.map